<script setup>
import {
    nextTick, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted, ref, computed, watch,
} from 'vue';

import useVuelidate from '@vuelidate/core';
import { useRouter } from 'vue-router';
import {
    useElementBounding,
    useIntersectionObserver,
    useMediaQuery,
    useWindowSize,
    watchOnce,
} from '@vueuse/core';
import {
    EVENTS,
    FUNNEL_TYPES,
    FUNNEL_STEP,
    track,
} from '../../utils/tracking';
import {
    log,
} from '../../utils/actionLogger';
import config from '../../config';
import { PAGE_CUSTOM_FIELDS, PAGE_PREVIEW, PAGE_SENDING } from '../../routes';

import AppButton from '../atoms/app/AppButton.vue';
import AppIcon from '../atoms/app/AppIcon.vue';
import AppLink from '../atoms/app/AppLink.vue';
import ApplicationLetter from '../organisms/ApplicationLetter.vue';
import FileUpload from '../organisms/FileUpload.vue';
import TheFooter from '../organisms/TheFooter.vue';
import GdprPolicies from '../organisms/GdprPolicies.vue';
import LayoutDefault from '../layouts/LayoutDefault.vue';
import PersonalData from '../organisms/PersonalData.vue';
import AppTile from '../molecules/app/AppTile.vue';
import TextHeadline from '../atoms/app/text/TextHeadline.vue';

import { $unitTestSpy } from '../../utils/unit-test-spy';
import { useBreakpoints } from '../../composables/breakpoint';
import { useErrorStore } from '../../stores/error-store';
import { useModalStore } from '../../stores/modal-store';
import { usePayloadStore } from '../../stores/payload-store';
import { CV_TYPES, useApplicationStore } from '../../stores/application-store';
import { useJobDataStore } from '../../stores/job-store';
import { useAuth } from '../../stores/auth-store';
import { useProfile } from '../../stores/profile-store';
import ApplicationSummary from '../organisms/ApplicationSummary.vue';
import KeyFactBox from '../organisms/KeyFactBox.vue';
import CustomFields from '../organisms/CustomFields.vue';
import { scrollToFirstError } from '../../utils/scrollToFirstError';
import WebViewOnly from '../webview/WebViewOnly.vue';
import { useWebViewContext } from '../../composables/webview-context';
import AppInfoBox from '../molecules/app/AppInfoBox.vue';
import CvSelection from '../organisms/CvSelection.vue';

const props = defineProps({
    previewMode: {
        type: Boolean,
        default: false,
    },
    customFieldsMode: {
        type: Boolean,
        default: false,
    },
});

const router = useRouter();

const backLabel = 'Zurück';
const previewLabel = 'Vorschau ansehen';

// Template refs
const root = ref(null);
const fileUpload = ref(null);
const fileUploadTile = ref(null);
const cvSelectionTile = ref(null);

const fileUploadTileHtmlElement = computed(() => fileUploadTile.value?.$el);
const cvSelectionTileHtmlElement = computed(() => cvSelectionTile.value?.$el);

const { isMobile, isMediumScreen } = useBreakpoints();

const application = useApplicationStore();
const errors = useErrorStore();
const modals = useModalStore();
const payload = usePayloadStore();
const jobData = useJobDataStore();
const auth = useAuth();
const profile = useProfile();

// Job has no custom fields, or we're in custom fields mode, or we're in preview mode
const isFinalStep = computed(() => !jobData.hasCustomFields || props.customFieldsMode || props.previewMode);

const trackingElementDetail = computed(() => {
    if (props.previewMode && jobData.hasCustomFields) return 'preview custom fields';
    if (props.previewMode) return 'preview';
    return 'custom fields';
});

function showLeavingAlert(e) {
    if (application.isDirty) {
        // Chrome requires a return value.
        // Only Internet Explorer can set a custom text. No joke.
        // Safari and Firefox need to prevent the default behavior.
        e.preventDefault();
        e.returnValue = '';
    }
    // Returning nothing means, the leaving modal should not be shown.
}

async function trackGtmEvent() {
    track({
        event: EVENTS.funnel,
        funnelType: FUNNEL_TYPES.application,
        funnelStep: FUNNEL_STEP.form,
        jobId: payload.jobId,
    });
}

// Close the gdpr modal on error
watch(() => errors.general, () => {
    if (errors.general) {
        window.scrollTo(0, 0);
        modals.gdpr = false;
    }
});

watch(() => errors.lauraHeidelbeereException, () => {
    if (errors.lauraHeidelbeereException) {
        fileUpload.value.setLauraHeidelbeereValidationMessage();
    }
});

const userCanMakeChanges = ref(true);
async function proceedToStep(step) {
    userCanMakeChanges.value = false;
    await router.push({
        name: step,
    });
    userCanMakeChanges.value = true;
}

function receiveMessageFromKarl(event) {
    if (event.data.type === 'event' && event.data.action === 'close') {
        window.location.reload();
    }
}

const v$ = useVuelidate();
const isCurrentlyUploading = computed(() => v$.value.attachments?.uploadingFiles.$invalid);

/* Preview mode functionality */
const previewButtonDisabled = computed(() => isCurrentlyUploading.value);
const previewModeAvailable = computed(() => !v$.value.applicationLetter?.$invalid &&
    !v$.value.attachments?.$invalid &&
    !v$.value.personalData?.$invalid &&
    !v$.value.cv?.$invalid &&
    !v$.value.cv?.$invalid &&
    !v$.value.customFields?.$invalid);

/* Next step functionality */
const customFieldsStepAvailable = computed(() => !v$.value.applicationLetter?.$invalid &&
    !v$.value.attachments?.$invalid &&
    !v$.value.personalData?.$invalid &&
    !v$.value.cv?.$invalid &&
    !v$.value.cv?.$invalid);

const mobilePreviewButtonVisible = computed(() => (!props.previewMode && isMobile.value) && isFinalStep.value);
const tabletPreviewButtonVisible = computed(() => (!props.previewMode && isMediumScreen.value) && isFinalStep.value);

watch(() => props.previewMode, (isPreview) => {
    if (isPreview) application.hasSeenPreview = true;
});

function handleCustomFieldsStepClick() {
    if (customFieldsStepAvailable.value) {
        proceedToStep(PAGE_CUSTOM_FIELDS);
    } else {
        v$.value.$reset();
        v$.value.applicationLetter?.$touch();
        v$.value.attachments?.$touch();
        v$.value.personalData?.$touch();
        v$.value.cv?.$touch();

        nextTick(scrollToFirstError);
    }
}

function handlePreviewClick() {
    if (previewModeAvailable.value) {
        proceedToStep(PAGE_PREVIEW);
    } else {
        v$.value.$reset();
        v$.value.applicationLetter?.$touch();
        v$.value.attachments?.$touch();
        v$.value.personalData?.$touch();
        v$.value.cv?.$touch();
        v$.value.customFields?.$touch();

        scrollToFirstError();
    }
}

function validateAndApply() {
    $unitTestSpy();

    // Check validation before modal is opened
    v$.value.$touch();

    if (!v$.value.$error) {
        proceedToStep(PAGE_SENDING);
    } else {
        const hasError = errorUid => v$.value.$errors.some(err => err.$uid === errorUid);
        // Gather tracking information
        const validationEvents = {
            'no-email': hasError('email-required') || hasError('email-email'),
            'invalid-email': hasError('email-maxLength'),
            'existing-email': hasError('email-available'),
            'no-firstname': hasError('firstname-required'),
            'no-lastname': hasError('lastname-required'),
            'firstname-max-char-count': hasError('firstname-maxLength'),
            'lastname-max-char-count': hasError('lastname-maxLength'),
            'max-filesize': hasError('attachments-maxSize'),
            'text-change-firstname': hasError('applicationLetter-placeholderFirstname'),
            'text-change-lastname': hasError('applicationLetter-placeholderLastname'),
            'text-max-char-count': hasError('applicationLetter-maxLength'),
            'text-no-text': hasError('applicationLetter-required'),
            'no-gdpr-consent': hasError('generalPolicy-checked'),
            'cv-rendering-failed': hasError('renderError-noRenderError'),
        };
        const validationErrors = [];
        Object.keys(validationEvents).forEach((key) => {
            if (validationEvents[key]) validationErrors.push(key);
        });
        const elementDetail = validationErrors.join(', ');

        track({
            element: 'AP_F: Submit - Error',
            elementDetail,
        });

        scrollToFirstError();
    }
}

/* form apply button behavior for non-splitview - sticky when it was visible once & not in viewport */
const formApplyButton = ref(null);
const formApplyButtonWasVisible = ref(false);
const stickyThreshold = ref(undefined);

// Media query is result of design specifications, see $splitview-breakpoint in SCSS

const SPLITVIEW_BREAKPOINT_IN_REM = 61;
const isSplitview = useMediaQuery(`(min-width: ${SPLITVIEW_BREAKPOINT_IN_REM}rem)`);
useIntersectionObserver(formApplyButton, ([{ isIntersecting, boundingClientRect }]) => {
    if (isIntersecting) {
        formApplyButtonWasVisible.value = true;
        stickyThreshold.value = boundingClientRect.bottom;
    }
}, { threshold: 1 });

const { height } = useWindowSize();
const { bottom } = useElementBounding(formApplyButton);
const formApplyButtonBelowViewport = computed(() => bottom.value >= height.value);

const isNextStepDisabled = computed(() => isCurrentlyUploading.value);
const companyName = computed(() => (jobData.isChiffre ? 'verdecktem Arbeitgeber' : jobData.companyName));
const formTitle = computed(() => (props.previewMode ? 'Bewerbungsvorschau' : `Bewerbung bei ${companyName.value}`));

onMounted(async () => {
    log('application-form-viewed', {
        status: auth.isLoggedIn ? 'logged-in' : 'logged-out',
        channel: isMobile.value ? 'mobile web' : 'web',
    });

    // Add recaptcha to page form if it was not already added.
    if (config.googleRecaptcha.active &&
        !document.querySelector('script[src*="https://www.google.com/recaptcha"]')
    ) {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${config.googleRecaptcha.siteKey}`;
        script.defer = true;
        script.async = true;
        if (document.body) {
            document.body.appendChild(script);
        }
    }

    trackGtmEvent();
});

/* Track if the form was valid at least once */
const wasValid = ref(false);
watch(() => v$.value.$invalid, (isInvalid) => {
    if (isInvalid) return;
    wasValid.value = true;
});

// Reset wasValid on page change, e.g. when entering custom fields mode or navigating back
watch(() => props.customFieldsMode, () => {
    wasValid.value = false;
});

// Track custom fields funnel
watchOnce(() => props.customFieldsMode, (isCustomFieldsMode) => {
    if (isCustomFieldsMode) {
        track({
            event: EVENTS.funnel,
            funnelType: FUNNEL_TYPES.application,
            funnelStep: FUNNEL_STEP.customFields,
        });
    }
});

const formApplyButtonIsStickyEnabled = computed(() => wasValid.value && !isSplitview.value);
const formApplyButtonIsSticky = computed(() => formApplyButtonIsStickyEnabled.value &&
    formApplyButtonBelowViewport.value && formApplyButtonWasVisible.value);

onBeforeMount(async () => {
    // The leaving alert is ignored for acceptance tests.
    if (config.leavingAlert.active) {
        window.addEventListener('beforeunload', showLeavingAlert);
    }

    await application.fetchCv();

    if (profile.hasProfile && application.isNoCvSelected) {
        application.selectedCvType = CV_TYPES.KARRIEREAT;
    }

    // Edge case: if user selected k.at but is no longer logged in, for example after page reload, unselect k.at CV.
    // Otherwise, the FE validation will not trigger because data-wise, the k.at CV is selected and valid
    const user = useAuth();
    if (!user.isLoggedIn && application.isNoCvSelected) {
        application.selectedCvType = CV_TYPES.NONE;
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('message', event => receiveMessageFromKarl(event));

    // The leaving alert is ignored for acceptance tests.
    if (config.leavingAlert.active) {
        window.removeEventListener('beforeunload', showLeavingAlert);
    }
});

onUnmounted(() => {
    // Do not display the recaptcha on all other pages.
    const recaptcha = document.querySelector('.grecaptcha-badge');
    if (recaptcha) {
        recaptcha.setAttribute('style', 'display: none !important');
    }
});

/* Web view */
const { appAuthorizationFailed } = useWebViewContext();
</script>

<template>
    <LayoutDefault ref="root">
        <template #main>
            <div class="p-pageForm">
                <div>
                    <div class="p-pageForm__wrap">
                        <TextHeadline
                            variant="lg-500"
                            class="mb-2xl"
                        >
                            {{ formTitle }}
                        </TextHeadline>
                        <div class="flex gap-l m:gap-2xl flex-col splitview:flex-row">
                            <div
                                :inert="!userCanMakeChanges"
                                class="p-pageForm__applicationForm"
                            >
                                <AppInfoBox
                                    v-if="errors.general"
                                    error
                                    data-qa="general error message"
                                >
                                    <div data-qa="error box">
                                        Es ist ein Fehler aufgetreten. Bitte versuche es noch einmal.
                                    </div>
                                </AppInfoBox>
                                <WebViewOnly>
                                    <AppInfoBox
                                        v-if="appAuthorizationFailed"
                                        warning
                                    >
                                        Es ist ein unerwarteter Fehler aufgetreten und du wurdest abgemeldet.
                                        Melde dich erneut an, um das Problem zu beheben.
                                    </AppInfoBox>
                                </WebViewOnly>
                                <div :class="{ 'border border-solid border-gray-300 rounded-md': !previewMode }">
                                    <template v-if="previewMode">
                                        <ApplicationSummary class="mb-l"/>
                                    </template>
                                    <template v-else-if="customFieldsMode">
                                        <AppTile>
                                            <template #title>
                                                Von {{ payload.companyName }} gestellte Fragen
                                            </template>
                                            <CustomFields/>
                                        </AppTile>
                                    </template>
                                    <template v-else>
                                        <div>
                                            <AppTile>
                                                <template #title>
                                                    Persönliche Daten
                                                </template>
                                                <PersonalData
                                                    data-qa="personal data"
                                                />
                                            </AppTile>
                                            <AppTile ref="cvSelectionTile">
                                                <template #title>
                                                    Lebenslauf *
                                                </template>
                                                <CvSelection
                                                    :dropzone-html-element="cvSelectionTileHtmlElement"
                                                />
                                            </AppTile>
                                            <AppTile ref="fileUploadTile">
                                                <template #title>
                                                    Sonstige Dokumente
                                                </template>
                                                <FileUpload
                                                    ref="fileUpload"
                                                    data-qa="file upload"
                                                    :dropzone-html-element="fileUploadTileHtmlElement"
                                                />
                                            </AppTile>
                                            <AppTile>
                                                <template #title>
                                                    Anmerkungen
                                                </template>
                                                <template #actions>
                                                    <AppLink
                                                        v-if="application.showApplicationLetter"
                                                        tag="button"
                                                        :secondary="true"
                                                        data-qa="remove application letter"
                                                        class="flex gap-s link"
                                                        data-gtm-element="AP_F: Remarks"
                                                        data-gtm-element-detail="Removed"
                                                        @click="application.removeApplicationLetter"
                                                    >
                                                        <AppIcon
                                                            name="dustbin"
                                                            size="xl"
                                                        />
                                                        <template
                                                            v-if="isMobile"
                                                        >
                                                            Entfernen
                                                        </template>
                                                        <template
                                                            v-else
                                                        >
                                                            Anmerkung entfernen
                                                        </template>
                                                    </AppLink>
                                                </template>
                                                <ApplicationLetter/>
                                            </AppTile>
                                        </div>
                                    </template>
                                    <AppTile
                                        v-if="isFinalStep"
                                        :class="{ 'border border-solid border-gray-300': previewMode }"
                                    >
                                        <template #title>
                                            Erklärungen
                                        </template>
                                        <GdprPolicies
                                            data-qa="gdpr policies"
                                        />
                                    </AppTile>
                                </div>
                                <AppButton
                                    v-if="mobilePreviewButtonVisible"
                                    v-track-visibility="{
                                        element: 'AP_F: Preview Button',
                                        elementDetail: jobData.hasCustomFields ? 'custom fields' : null,
                                    }"
                                    :disabled="previewButtonDisabled"
                                    :data-qa="'mobile preview button' + (previewModeAvailable ? '' : ' disabled')"
                                    data-gtm-element="AP_F: Open preview"
                                    :data-gtm-element-detail="jobData.hasCustomFields ? 'custom fields' : null"
                                    class="mt-m button button-transparent"
                                    full
                                    @click="handlePreviewClick"
                                >
                                    {{ previewLabel }}
                                </AppButton>
                                <div
                                    ref="formApplyButton"
                                    class="p-pageForm__applyButtonWrapper flex gap-l"
                                    :class="{
                                        'p-pageForm__applyButtonWrapper--stickyEnabled': formApplyButtonIsStickyEnabled,
                                        'p-pageForm__applyButtonWrapper--sticky': formApplyButtonIsSticky,
                                        '-mt-m': isMobile && !previewMode,
                                        'm:justify-start': !previewMode,
                                        'm:justify-between': previewMode,
                                    }"
                                >
                                    <AppButton
                                        v-if="(previewMode || customFieldsMode) && isMobile"
                                        class="px-m aspect-square"
                                        data-gtm-element="AP_F: Edit application"
                                        :data-gtm-element-detail="trackingElementDetail"
                                        data-qa="mobile edit application button"
                                        outline-variant
                                        @click="router.back"
                                    >
                                        <AppIcon
                                            name="arrowhead"
                                            size="xl"
                                            class="rotate-180"
                                        />
                                    </AppButton>
                                    <AppLink
                                        v-if="previewMode && isMediumScreen"
                                        tag="button"
                                        class="hover:no-underline flex gap-m items-center link"
                                        data-gtm-element="AP_F: Edit application"
                                        :data-gtm-element-detail="trackingElementDetail"
                                        data-qa="edit application button"
                                        @click="router.back"
                                    >
                                        <AppIcon
                                            name="arrowhead"
                                            size="xl"
                                            class="rotate-180"
                                        />
                                        Eingaben ändern
                                    </AppLink>
                                    <AppLink
                                        v-else-if="customFieldsMode && isMediumScreen"
                                        tag="button"
                                        class="hover:no-underline flex gap-m items-center mr-auto link"
                                        data-gtm-element="AP_F: Edit application"
                                        :data-gtm-element-detail="trackingElementDetail"
                                        data-qa="edit application button"
                                        @click="router.back"
                                    >
                                        <AppIcon
                                            name="arrowhead"
                                            size="xl"
                                            class="rotate-180"
                                        />
                                        {{ backLabel }}
                                    </AppLink>
                                    <AppButton
                                        v-if="tabletPreviewButtonVisible"
                                        v-track-visibility="{
                                            element: 'AP_F: Preview Button',
                                            elementDetail: jobData.hasCustomFields ? 'custom fields' : null,
                                        }"
                                        :disabled="previewButtonDisabled"
                                        :data-qa="'preview button' + (previewModeAvailable ? '' : ' disabled')"
                                        data-gtm-element="AP_F: Open preview"
                                        :data-gtm-element-detail="jobData.hasCustomFields ? 'custom fields' : null"
                                        class="button button-transparent"
                                        @click="handlePreviewClick"
                                    >
                                        {{ previewLabel }}
                                    </AppButton>
                                    <AppButton
                                        v-if="isFinalStep"
                                        v-track-visibility="{
                                            element: 'AP_F: Job Apply Button',
                                            elementDetail: jobData.hasCustomFields ? 'custom fields' : null,
                                        }"
                                        data-gtm-element="AP_F: Job Apply Button"
                                        :data-gtm-element-detail="jobData.hasCustomFields ? 'custom fields' : null"
                                        :data-qa="isNextStepDisabled ? 'apply disabled' : 'apply'"
                                        :disabled="isNextStepDisabled"
                                        :full="isMobile"
                                        :loading="!userCanMakeChanges"
                                        class="flex gap-xs items-center justify-center shrink"
                                        @click="
                                            validateAndApply();
                                            log('application-form-continue');
                                        "
                                    >
                                        Bewerbung abschließen
                                    </AppButton>
                                    <AppButton
                                        v-else
                                        v-track-visibility="{
                                            element: 'AP_F: Next Step Button',
                                        }"
                                        data-gtm-element="AP_F: Next Step Button"
                                        :data-qa="isNextStepDisabled ? 'to custom fields disabled' : 'to custom fields'"
                                        :disabled="isNextStepDisabled"
                                        :full="isMobile"
                                        class="button button-green"
                                        @click="handleCustomFieldsStepClick"
                                    >
                                        Nächster Schritt
                                    </AppButton>
                                </div>
                            </div>
                            <div class="p-pageForm__applicationInfo">
                                <KeyFactBox
                                    :preview-mode="previewMode"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <TheFooter
                variant="small"
                data-qa="small footer"
            />
        </template>
    </LayoutDefault>
</template>

<style lang="scss">
@use 'sass:math';

@import '~@karriereat/global-styles/scss/settings/breakpoint';
@import '~@karriereat/global-styles/scss/settings/spacing';
@import '~@karriereat/global-styles/scss/tools/wrap';
@import '~@karriereat/global-styles/scss/objects/media';

// 1. normalize margin, so text and button are horizontal centered aligned
.p-pageForm {
    $external-link-icon-visual-correction: 1px;
    $apply-wrapper-box-shadow: 0 -5px 10px 0 rgba(0, 0, 0, 0.1);
    $application-form-width: 30rem;
    $application-form-gap: 1rem;
    $application-info-width: 28rem;
    $splitview-breakpoint: 1rem + 1rem + $application-form-gap + $application-form-width + $application-info-width;

    padding-top: $k-spacing--2xl;
    padding-bottom: $k-spacing--5xl;
    background-color: var(--k-white);

    @media (min-width: $splitview-breakpoint) {
        padding-top: $k-spacing--4xl;
        padding-bottom: $k-spacing--5xl;
    }

    &__wrap {
        @include k-wrap(math.div(12, 12));
    }

    &__applicationForm {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        gap: $k-spacing--l;
        order: 1;
    }

    &__applicationInfo {
        order: 0;

        @media (min-width: $splitview-breakpoint) {
            flex-shrink: 0;
            flex-direction: row;
            order: 2;
            width: $application-info-width;
        }
    }

    &__applyButtonWrapper {
        position: sticky;
        bottom: -$k-spacing--5xl;
        margin-right: -$k-spacing--l;
        margin-left: -$k-spacing--l;
        padding: $k-spacing--l;
        transition: all 0.3s ease;

        &--stickyEnabled {
            bottom: 0;
        }

        &--sticky {
            background-color: var(--k-white);
            box-shadow: $apply-wrapper-box-shadow;
        }
    }
}
// 2. Overwrite recaptcha-badge style, so it is above the footer
.grecaptcha-badge {
    bottom: 75px !important;
}
</style>
